import React, { useState } from 'react';
import { TrendingUp, Award, Activity } from 'lucide-react';
import Card from '../ui/Card';
import Input from '../ui/Input';
import Button from '../ui/Button';
import InfoTooltip from '../ui/InfoTooltip';
import ResultGauge from '../ui/ResultGauge';
import { calculateCGPA } from '../../utils/gpaLogic';
import { toast } from 'react-toastify';

const CGPACalculator = () => {
  const [data, setData] = useState({
    currentCGPA: '',
    completedCreditHours: '',
    currentSemesterGPA: '',
    currentSemesterCreditHours: ''
  });
  
  const [result, setResult] = useState(null);

  const handleCalculate = () => {
    try {
      const res = calculateCGPA(data);
      setResult(res);
      toast.success("CGPA updated!");
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-700">
      {/* Input Sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Current Standing Card */}
        <div className="bg-white dark:bg-[#0f1535] p-6 rounded-[2.5rem] border border-slate-100 dark:border-indigo-500/10 shadow-sm relative overflow-hidden group hover:shadow-xl hover:shadow-indigo-500/5 transition-all duration-500">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 rounded-2xl">
              <Award size={20} />
            </div>
            <h3 className="font-black text-slate-900 dark:text-white tracking-tight">Current Standing</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-1 mb-2">
                <label className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Current CGPA</label>
                <InfoTooltip content="Your CGPA from all previous semesters (0.00 - 4.00)" />
              </div>
              <Input 
                value={data.currentCGPA}
                onChange={(e) => setData({...data, currentCGPA: e.target.value})}
                placeholder="0.00"
                inputMode="decimal"
                className="text-lg font-bold"
              />
            </div>
            
            <div>
              <div className="flex items-center gap-1 mb-2">
                <label className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Completed CH</label>
                <InfoTooltip content="Total credit hours you've completed so far" />
              </div>
              <Input 
                value={data.completedCreditHours}
                onChange={(e) => setData({...data, completedCreditHours: e.target.value})}
                placeholder="0"
                inputMode="numeric"
                className="text-lg font-bold"
              />
            </div>
          </div>
        </div>

        {/* Current Semester Card */}
        <div className="bg-white dark:bg-[#0f1535] p-6 rounded-[2.5rem] border border-slate-100 dark:border-indigo-500/10 shadow-sm relative overflow-hidden group hover:shadow-xl hover:shadow-purple-500/5 transition-all duration-500">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-purple-50 dark:bg-purple-500/10 text-purple-600 rounded-2xl">
              <Activity size={20} />
            </div>
            <h3 className="font-black text-slate-900 dark:text-white tracking-tight">This Semester</h3>
          </div>
          
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-1 mb-2">
                <label className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Semester GPA</label>
                <InfoTooltip content="Your GPA for this semester only (0.00 - 4.00)" />
              </div>
              <Input 
                value={data.currentSemesterGPA}
                onChange={(e) => setData({...data, currentSemesterGPA: e.target.value})}
                placeholder="0.00"
                inputMode="decimal"
                className="text-lg font-bold"
              />
            </div>
            
            <div>
              <div className="flex items-center gap-1 mb-2">
                <label className="text-xs font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest ml-1">Semester CH</label>
                <InfoTooltip content="Credit hours for this semester only" />
              </div>
              <Input 
                value={data.currentSemesterCreditHours}
                onChange={(e) => setData({...data, currentSemesterCreditHours: e.target.value})}
                placeholder="0"
                inputMode="numeric"
                className="text-lg font-bold"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center gap-8">
        <Button 
          onClick={handleCalculate} 
          className="w-full md:w-auto px-16 py-4 rounded-2xl shadow-lg shadow-indigo-500/20 active:scale-95 transition-all"
        >
          Calculate Projected CGPA
        </Button>

        {result && (
          <div className="w-full max-w-lg animate-in zoom-in-95 duration-500">
            <div className="bg-white dark:bg-[#0b0f2a] p-8 rounded-[3rem] border border-slate-100 dark:border-indigo-500/10 shadow-2xl relative overflow-hidden flex flex-col items-center">
              <ResultGauge 
                value={result.newCGPA} 
                label="Projected CGPA" 
                subLabel={result.improvement.startsWith('+') ? `Boost: ${result.improvement}` : `Trend: ${result.improvement}`}
                size={240}
              />
              
              <div className="mt-8 grid grid-cols-2 gap-4 w-full pt-8 border-t border-slate-100 dark:border-indigo-500/5">
                <div className="text-center">
                  <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">Old CGPA</span>
                  <span className="text-xl font-bold text-slate-600 dark:text-slate-400">{parseFloat(data.currentCGPA).toFixed(2)}</span>
                </div>
                <div className="text-center border-l border-slate-100 dark:border-indigo-500/5">
                  <span className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest block mb-1">Total CH</span>
                  <span className="text-xl font-bold text-slate-600 dark:text-slate-400">
                    {parseInt(data.completedCreditHours) + parseInt(data.currentSemesterCreditHours)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CGPACalculator;
